import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Vote, FileText, Eye, CheckCircle2, ImageIcon, X, Share2, Download, LayoutGrid, Lock, RotateCcw, ShieldAlert, Landmark } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { api, fileUrl } from '../lib/api';
import { setPageMeta } from '../lib/seo';
import { PageHeader, SectionHeader, RichSection } from '../components/Shared';
import { NextStepBand } from '../components/NextStepBand';
import { FactBadge, FactCard, SourceCite, PositionBox, QuestionBox, FaqList } from '../components/FactCheck';

const ACCESSED = 'August 25, 2026';

const WHY_POINTS = [
  { icon: FileText, title: 'Auditability', body: 'A marked paper ballot is a physical record. It can be counted again, checked again, and audited by hand, years later if needed.' },
  { icon: RotateCcw, title: 'Manual recounts', body: 'With paper, a close race can be recounted by people in a room, watched by scrutineers. Everyone can see how the result was reached.' },
  { icon: Eye, title: 'Public confidence', body: 'Trust grows when regular people can watch the count and understand it, with no technical background needed.' },
  { icon: CheckCircle2, title: 'Election transparency', body: 'Paper counting happens in the open. Observers can witness every step from ballot box to final tally.' },
  { icon: Lock, title: 'Ballot secrecy', body: 'A paper ballot marked privately in a voting screen keeps your choice between you and the ballot box.' },
  { icon: ShieldAlert, title: 'Cybersecurity', body: 'A paper ballot cannot be hacked, deleted, or altered remotely. Reducing reliance on internet systems reduces cyber risk.' },
];

const RESEARCH_SECTIONS = [
  {
    id: 'simply-voting', title: 'Simply Voting (the 2026 vendor)',
    facts: 'For the 2026 municipal election, the City of Kawartha Lakes selected internet and telephone voting, with Simply Voting, a Canadian company, providing the technology. The City reports the vendor is also used by many other Ontario municipalities for 2026.',
    cite: { source: 'City of Kawartha Lakes, Electronic Voting page', year: '2026', accessed: ACCESSED, url: 'https://www.kawarthalakes.ca/government-administration/elections/electronic-voting/' },
    question: 'When a private vendor runs the voting system, how do residents independently verify the count?',
  },
  {
    id: 'mfippa', title: 'MFIPPA and privacy',
    facts: 'The Municipal Freedom of Information and Protection of Privacy Act (MFIPPA) sets the rules for how Ontario municipalities collect, use, and protect personal information, and gives residents the right to request municipal records.',
    cite: { source: 'Municipal Freedom of Information and Protection of Privacy Act (Ontario)', year: '1990, as amended', accessed: ACCESSED, url: 'https://www.ontario.ca/laws/statute/90m56' },
    question: 'What voter information does an online voting system collect, where is it stored, and who can access it?',
  },
  {
    id: 'evoting-security', title: 'Electronic voting security',
    facts: 'Internet voting depends on software, servers, networks, and vendor processes that voters cannot directly observe. Ontario has no provincial certification standard specific to municipal internet voting systems; each municipality chooses and contracts its own method under the Municipal Elections Act.',
    cite: { source: 'Municipal Elections Act, 1996 (Ontario), s. 42 (alternative voting methods)', year: '1996, as amended', accessed: ACCESSED, url: 'https://www.ontario.ca/laws/statute/96m32' },
    question: 'If something goes wrong inside the system, how would we know, and how would we prove it?',
  },
  {
    id: 'ballot-secrecy', title: 'Ballot secrecy',
    facts: 'The Municipal Elections Act requires the secrecy of the vote. With paper ballots marked privately at a voting place, secrecy is physical and simple to understand. With remote voting, secrecy depends on the technology and on the environment where the person votes.',
    cite: { source: 'Municipal Elections Act, 1996 (Ontario)', year: '1996, as amended', accessed: ACCESSED, url: 'https://www.ontario.ca/laws/statute/96m32' },
    question: 'How is a secret vote guaranteed when ballots are cast on personal devices outside a controlled voting place?',
  },
  {
    id: 'auditability', title: 'Auditability',
    facts: 'A physical ballot creates a permanent, human-readable record that exists independently of any computer system. Digital votes exist as records inside vendor systems.',
    cite: { source: 'Government of Ontario, Municipal Elections Act framework', year: '2026', accessed: ACCESSED, url: 'https://www.ontario.ca/laws/statute/96m32' },
    question: 'Without paper, what physical evidence of each vote exists for a court, an auditor, or a recount to examine?',
  },
  {
    id: 'recounts', title: 'Recounts',
    facts: 'The Municipal Elections Act provides for recounts, including automatic recounts in tied races and recounts ordered by council or a court. How meaningful a recount can be depends on what records exist to recount.',
    cite: { source: 'Municipal Elections Act, 1996 (Ontario), recount provisions', year: '1996, as amended', accessed: ACCESSED, url: 'https://www.ontario.ca/laws/statute/96m32' },
    question: 'In an internet election, does a recount simply re-run the same software over the same data, and is that a true recount?',
  },
  {
    id: 'election-records', title: 'Election records',
    facts: 'The Municipal Elections Act requires the clerk to retain election records for a defined period after an election. What those records contain differs between paper elections and electronic elections.',
    cite: { source: 'Municipal Elections Act, 1996 (Ontario), records provisions', year: '1996, as amended', accessed: ACCESSED, url: 'https://www.ontario.ca/laws/statute/96m32' },
    question: 'What exactly is retained after an internet election, and could an independent person verify results from it?',
  },
  {
    id: 'cybersecurity', title: 'Cybersecurity',
    facts: 'Canadian security authorities publish guidance on cyber threats to democratic processes, and online systems of every kind face phishing, credential theft, denial-of-service, and software supply-chain risks.',
    cite: { source: 'Canadian Centre for Cyber Security, Cyber Threats to Canada\'s Democratic Process', year: '2025 update', accessed: ACCESSED, url: 'https://www.cyber.gc.ca/en/guidance/cyber-threats-canadas-democratic-process' },
    question: 'Why accept avoidable cyber risk in the single most important thing a municipality does: electing its government?',
  },
  {
    id: 'official-procedures', title: 'Official election procedures',
    facts: 'Council authorized internet and telephone voting for 2026 (By-law No. 2025-136, passed November 18, 2025). The voting period runs October 16, 2026 at 9:00 a.m. to October 26, 2026 at 8:00 p.m. The Clerk\'s procedures identify Simply Voting Inc. and provide for system testing and a Clerk-appointed auditor. The City has stated paper ballots will not be available, and staffed voting help locations will offer devices for residents without internet access.',
    cite: { source: 'City of Kawartha Lakes By-law No. 2025-136 and Clerk\'s election procedures', year: '2025', accessed: ACCESSED, url: 'https://www.kawarthalakes.ca/government-administration/elections/' },
    question: 'If help locations already need staff and devices, why not offer a real paper ballot option at the same time?',
  },
];

const FAQS = [
  { q: 'Is Steve against technology?', a: 'No. Steve builds technology, including PITA. This is about using the right tool for the job. For elections, a physical paper ballot creates the strongest public record.' },
  { q: 'Would paper ballots mean losing online convenience?', a: 'Steve is asking for the paper ballot CHOICE to be restored. Voters who prefer other approved methods could still use them. The point is that no resident should be denied the option of marking a physical ballot.' },
  { q: 'Why does this have to happen before the 2026 election?', a: 'Voting methods are decided by council in advance of each election, and the City has already selected internet and telephone voting for 2026. The window to add a paper option is now, not after the election.' },
  { q: 'Did Kawartha Lakes ever have paper ballots?', a: 'The City reports it has not used traditional polling station voting since amalgamation, and has used internet and telephone voting in 2018, 2022, and now for 2026.' },
  { q: 'What if the paper ballot choice is not restored?', a: 'Then the people still need transparency. That is why Steve also created Witness Your Vote, an independent civic transparency initiative. It is not a replacement for the official election.' },
];

const PosterPlaceholder = ({ part, big = false }) => (
  <div className={`w-full ${big ? 'aspect-[3/4]' : 'aspect-[3/4]'} flex flex-col items-center justify-center gap-2 bg-gradient-to-b from-[#1a2744] to-[#22345c] ${big ? 'p-8' : 'p-3'}`}>
    <Vote className={`${big ? 'h-12 w-12' : 'h-7 w-7'} text-[#C9A84C]`} />
    <p className={`text-white font-black ${big ? 'text-3xl' : 'text-lg'} leading-none`}>{part}</p>
    <p className={`${big ? 'text-sm' : 'text-[10px]'} font-bold uppercase tracking-wider text-slate-300 text-center leading-tight`}>Poster coming soon</p>
  </div>
);

/* Image with a graceful fallback: if the file behind a poster is ever missing,
   visitors see the numbered placeholder instead of a broken-image icon. */
const PosterImage = ({ src, alt, part, className, ...rest }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  if (failed) return <PosterPlaceholder part={part} />;
  return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} data-testid={`poster-image-${part}`} {...rest} />;
};

export default function PaperBallots() {
  const [posters, setPosters] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const touchX = useRef(null);
  const { part: partParam } = useParams();
  const seriesRef = useRef(null);

  useEffect(() => {
    api.get('/paper-ballot-posters').then((r) => setPosters(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // Individual poster URLs: /paper-ballots/:part opens that poster directly
  useEffect(() => {
    if (!posters.length || !partParam) return;
    const idx = posters.findIndex((p) => String(p.part) === String(partParam));
    if (idx >= 0) {
      setOpenIndex(idx);
      setTimeout(() => seriesRef.current?.scrollIntoView({ block: 'start' }), 50);
    }
  }, [posters, partParam]);

  const open = openIndex !== null ? posters[openIndex] : null;

  // URL sync without a router navigation (keeps scroll position while browsing)
  const syncUrl = (poster) => {
    window.history.replaceState(null, '', poster ? `/paper-ballots/${poster.part}` : '/paper-ballots');
  };
  const openAt = useCallback((idx) => {
    setOpenIndex(idx);
    syncUrl(idx === null ? null : posters[idx]);
  }, [posters]);
  const goPrev = useCallback(() => {
    setOpenIndex((i) => {
      if (i === null) return null;
      const n = (i - 1 + posters.length) % posters.length;
      syncUrl(posters[n]);
      return n;
    });
  }, [posters]);
  const goNext = useCallback(() => {
    setOpenIndex((i) => {
      if (i === null) return null;
      const n = (i + 1) % posters.length;
      syncUrl(posters[n]);
      return n;
    });
  }, [posters]);
  const closeViewer = useCallback(() => {
    setOpenIndex(null);
    syncUrl(null);
  }, []);

  // Per-poster page title, meta description, and social share tags
  useEffect(() => {
    if (open) {
      const title = open.share_title || `${open.title} | The Paper Ballot Series | Steve Lesperance`;
      const description = open.share_description || open.explanation ||
        `${open.title} of Steve Lesperance's 18-part Paper Ballot series. Restore the paper ballot choice before the 2026 municipal election.`;
      const img = open.share_image_url || open.image_url;
      const imgResolved = img ? fileUrl(img) : '';
      setPageMeta({
        title, description,
        ogImage: imgResolved ? (imgResolved.startsWith('http') ? imgResolved : `${window.location.origin}${imgResolved}`) : undefined,
        url: `${window.location.origin}/paper-ballots/${open.part}`,
      });
    } else {
      setPageMeta({
        title: 'Restore the Paper Ballot Choice | Steve Lesperance, Ward 8',
        description: 'Steve Lesperance is calling on Kawartha Lakes to restore the option of marking a physical paper ballot before the 2026 municipal election.',
        url: `${window.location.origin}/paper-ballots`,
      });
    }
  }, [open]);

  const sharePoster = async (p) => {
    // Server-side share route: crawlers get real Open Graph tags for THIS
    // poster; human visitors are redirected to /paper-ballots/{part}.
    const shareUrl = `${window.location.origin}/api/share/poster/${p.part}`;
    const text = p.share_description || `${p.title} of Steve Lesperance's 18-part Paper Ballot series. Restore the paper ballot choice before the 2026 municipal election.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: p.share_title || p.title, text, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(`${text} ${shareUrl}`);
        toast.success('Share link copied to clipboard');
      }
    } catch { /* user cancelled */ }
  };

  const posterSources = (p) => (p.sources_text || '').split('\n').map((line) => {
    const [label, url] = line.split('|').map((s) => (s || '').trim());
    return label && url ? { label, url } : null;
  }).filter(Boolean);

  return (
    <div>
      <PageHeader
        pageKey="paper-ballots"
        kicker="A MAJOR CAMPAIGN ISSUE"
        title="RESTORE THE PAPER BALLOT CHOICE. NOW."
        subtitle="BEFORE THE 2026 MUNICIPAL ELECTION. Steve is calling on Kawartha Lakes to restore the option of marking a physical paper ballot."
      />

      {/* Why paper ballots: plain language */}
      <section id="why" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="paper-ballots-why-section">
        <SectionHeader kicker="WHY IT MATTERS" title="Why paper ballots?" dek="Six plain-language reasons. No jargon." />
        <RichSection id="paper-ballots-sections.main_argument" className="mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {WHY_POINTS.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="card-lift" data-testid="paper-ballots-why-card">
              <CardContent className="p-5">
                <div className="h-10 w-10 rounded-lg bg-[#1a2744] flex items-center justify-center mb-3">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <p className="font-serif font-bold text-[#1a2744] leading-snug">{title}</p>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Why NOW: facts, clearly sourced */}
        <div className="mt-10">
          <SectionHeader kicker="WHY THIS ELECTION" title="Why it must be addressed now" />
          <RichSection id="paper-ballots-sections.why_now" className="mb-4" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FactCard kind="verified_fact" title="Kawartha Lakes has already chosen internet and telephone voting for 2026"
              cite={{ source: 'City of Kawartha Lakes, Electronic Voting page', year: '2026', accessed: ACCESSED, url: 'https://www.kawarthalakes.ca/government-administration/elections/electronic-voting/' }}>
              The City selected internet and telephone voting for the 2026 municipal election, delivered by the vendor Simply Voting, with a 10-day voting period.
            </FactCard>
            <FactCard kind="verified_fact" title="Voting methods are locked in by bylaw ahead of the election"
              cite={{ source: 'Municipal Elections Act, 1996 (Ontario), s. 42', year: '1996, as amended', accessed: ACCESSED, url: 'https://www.ontario.ca/laws/statute/96m32' }}>
              Alternative voting methods are authorized under section 42 of the Municipal Elections Act, which requires council to pass a bylaw in advance. Decisions about how we vote in 2026 are being made now, not on election day.
            </FactCard>
          </div>
          <div className="mt-4">
            <PositionBox title="Steve's first choice is restoring physical paper ballots for the 2026 municipal election." testId="paper-ballots-position-band">
              <p>Restore the paper ballot option so every voter can choose a physical, recountable, human-verifiable ballot. If municipalities continue without physical paper ballots, the people still need transparency. That is why Steve also created Witness Your Vote.</p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Button asChild className="h-11 bg-white hover:bg-slate-100 text-[#1a2744] font-bold btn-press" data-testid="paper-ballots-wyv-button"><Link to="/witness-your-vote">Learn About Witness Your Vote <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
                <Button asChild variant="outline" className="h-11 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press"><Link to="/elections">2026 Election Centre</Link></Button>
              </div>
            </PositionBox>
          </div>
        </div>
      </section>

      {/* 18-part poster series (admin-managed) */}
      <section id="series" ref={seriesRef} className="bg-white border-y border-slate-200 py-10 md:py-14 scroll-mt-20" data-testid="paper-ballots-series-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="THE CAMPAIGN" title="The 18-Part Paper Ballot Poster Series" dek="One part at a time, the series makes the case for restoring the paper ballot choice. Tap any poster to view, share, and read the sources." />
          <RichSection id="paper-ballots-sections.posters_intro" className="mb-5" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="poster-series-grid">
            {posters.map((p, idx) => (
              <button key={p.id} onClick={() => openAt(idx)}
                className="group text-left rounded-xl border border-slate-200 bg-[#F3F5F8] hover:border-[#24406f] transition-colors duration-150 overflow-hidden focus-visible:ring-2 focus-visible:ring-[#1a2744] focus-visible:ring-offset-2"
                data-testid={`poster-card-${p.part}`} aria-label={`Open poster ${p.title}`}>
                {p.image_url ? (
                  <PosterImage src={fileUrl(p.image_url)} alt={p.title} part={p.part} className="w-full aspect-[3/4] object-cover" loading="lazy" />
                ) : (
                  <PosterPlaceholder part={p.part} />
                )}
                <div className="p-2.5">
                  <p className="text-xs font-bold text-[#1a2744] truncate">{p.title}</p>
                </div>
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4" data-testid="poster-series-note">Official poster artwork is added as each part of the series is released.</p>
        </div>
      </section>

      {/* Poster viewer: prev / next / share / download / swipe */}
      <Dialog open={!!open} onOpenChange={(v) => { if (!v) closeViewer(); }}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="poster-viewer-dialog">
          <DialogTitle className="sr-only">{open ? `Paper ballot poster ${open.title}` : 'Poster viewer'}</DialogTitle>
          {open && (
            <div
              onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                if (touchX.current === null) return;
                const dx = e.changedTouches[0].clientX - touchX.current;
                if (dx > 60) goPrev(); else if (dx < -60) goNext();
                touchX.current = null;
              }}
            >
              <div className="relative">
                {open.image_url ? (
                  <PosterImage src={fileUrl(open.image_url)} alt={open.title} part={open.part} className="w-full h-auto max-h-[60vh] object-contain bg-[#1a2744]" />
                ) : (
                  <PosterPlaceholder part={open.part} big />
                )}
                <button onClick={goPrev} aria-label="Previous poster" className="absolute left-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/90 border border-slate-200 flex items-center justify-center hover:bg-white btn-press" data-testid="poster-viewer-prev">
                  <ArrowLeft className="h-5 w-5 text-[#1a2744]" />
                </button>
                <button onClick={goNext} aria-label="Next poster" className="absolute right-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/90 border border-slate-200 flex items-center justify-center hover:bg-white btn-press" data-testid="poster-viewer-next">
                  <ArrowRight className="h-5 w-5 text-[#1a2744]" />
                </button>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-serif font-black text-[#1a2744]" data-testid="poster-viewer-title">{open.title}</p>
                  <p className="text-xs font-bold text-slate-400">{open.part} / {posters.length}</p>
                </div>
                {open.explanation && <p className="text-sm text-slate-700 mt-2 leading-relaxed" data-testid="poster-viewer-explanation">{open.explanation}</p>}
                {!open.image_url && <p className="text-xs text-slate-500 mt-2">The official poster for this part will appear here when it is released.</p>}
                {posterSources(open).length > 0 && (
                  <div className="mt-3 space-y-1" data-testid="poster-viewer-sources">
                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Sources</p>
                    {posterSources(open).map((s, i) => (
                      <a key={`${s.url}-${i}`} href={s.url} target="_blank" rel="noopener noreferrer" className="block text-xs font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-2">{s.label}</a>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {open.article_url && (
                    <Button asChild variant="outline" size="sm" className="h-10 font-bold btn-press" data-testid="poster-viewer-article">
                      <a href={open.article_url} target={open.article_url.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">{open.article_label || 'Related article'}</a>
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="h-10 font-bold btn-press" onClick={() => sharePoster(open)} data-testid="poster-viewer-share">
                    <Share2 className="h-4 w-4 mr-1" /> Share
                  </Button>
                  {open.image_url && (
                    <Button asChild variant="outline" size="sm" className="h-10 font-bold btn-press" data-testid="poster-viewer-download">
                      <a href={fileUrl(open.image_url)} download>{'Download'}</a>
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="h-10 font-bold btn-press" onClick={closeViewer} data-testid="poster-viewer-viewall">
                    <LayoutGrid className="h-4 w-4 mr-1" /> View All
                  </Button>
                  <Button size="sm" className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press ml-auto" onClick={closeViewer} data-testid="poster-viewer-close">
                    <X className="h-4 w-4 mr-1" /> Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Research areas */}
      <section id="research" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="paper-ballots-research-section">
        <SectionHeader kicker="DO YOUR OWN RESEARCH" title="The research, area by area" dek="Facts are labelled and sourced. Steve's questions and opinions are labelled separately. Follow the links and check everything yourself." />
        <RichSection id="paper-ballots-sections.research_intro" className="mb-4" />
        <Accordion type="single" collapsible className="space-y-2" data-testid="research-accordion">
          {RESEARCH_SECTIONS.map((s) => (
            <AccordionItem key={s.id} value={s.id} className="bg-white rounded-xl border border-slate-200 px-4 sm:px-5 data-[state=open]:border-[#24406f]" data-testid={`research-item-${s.id}`}>
              <AccordionTrigger className="hover:no-underline py-4 text-left font-serif font-bold text-[#1a2744]">{s.title}</AccordionTrigger>
              <AccordionContent className="pb-5 space-y-3">
                <div>
                  <FactBadge kind="verified_fact" />
                  <p className="text-sm text-slate-700 leading-relaxed mt-2">{s.facts}</p>
                </div>
                <SourceCite {...s.cite} />
                <QuestionBox testId={`research-question-${s.id}`}>{s.question}</QuestionBox>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14" data-testid="paper-ballots-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="PLAIN ANSWERS" title="Common questions" />
          <RichSection id="paper-ballots-sections.faq_intro" className="mb-4" />
          <FaqList items={FAQS} testId="paper-ballots-faq" />
        </div>
      </section>

      {/* One clear next step */}
      <div className="pt-10">
        <RichSection id="paper-ballots-sections.cta_band" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-6" />
        <NextStepBand
          title="See the case, one poster at a time."
          buttonLabel="View the 18-Part Campaign"
          onClick={() => seriesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          secondaryLabel="Or learn about Witness Your Vote"
          secondaryTo="/witness-your-vote"
          testId="paper-ballots-next-step"
        />
      </div>
    </div>
  );
}
